import React, { useState, useEffect, useMemo } from 'react';
import { Formik } from 'formik';
import { default as Selector } from 'react-select';
import { useSelector } from 'react-redux';
import {
  Form,
  Button,
  Card,
  Row,
  Col,
  Collapse,
  Pagination,
  Spinner,
  Modal,
} from 'react-bootstrap';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';
import MaskedInput from 'react-text-mask';
import {
  FaSearch,
  FaEraser,
  FaPlus,
  FaMinus,
  FaSearchDollar,
  FaExclamationCircle,
  FaFileExcel,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import * as constants from '../../constant';
import DateMask from '../DateMask';
import * as api from '~/Services/Service';
import {
  format,
  formatarMonetario,
  definirStatusComissao,
  salvarEmExcel,
} from '~/Utils/Helpers';
import { IconButton } from '@material-ui/core';
import ModalDetalhesComissao from './ModalDetalhesComissao';
import AuthStorage from '~/Utils/AuthStorage';

const initialValues = {
  codigo_imobiliaria: '',
  codigo_corretor: '',
  codigo_prospecto: '',
  codigo_prevenda: '',
  codigo_empreendimento: '',
  quadra: '',
  lote: '',
  data_venda_inicio: '',
  data_venda_fim: '',
  data_proximo_pagamento_inicio: '',
  data_proximo_pagamento_fim: '',
  status_comissao: '',
  refinanciado: '',
};

const renderBadgeStatusComissao = (comissaoCancelada, comissaoBaixada) => {
  const status = definirStatusComissao(comissaoCancelada, comissaoBaixada);
  const badgeClass = {
    Cancelada: 'badge-danger',
    Recebida: 'badge-success',
    'A receber': 'badge-warning',
  };

  return (
    <span
      className={`badge ${badgeClass[status] || 'badge-secondary'}`}
      style={{ fontSize: '12px' }}
    >
      {status}
    </span>
  );
};

const CardVendasDetalhadas = ({}) => {
  const [maisFiltros, setMaisFiltros] = useState(false);
  const [vendas, setVendas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [abrirModalDetalhes, setAbrirModalDetalhes] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(12);
  const [totais, setTotais] = useState(null);
  const [linhaSelecionada, setLinhaSelecionada] = useState(null);
  const [exportandoExcel, setExportandoExcel] = useState(false);

  const empreendimentos = useSelector(state => state.empreendimentos);
  const gerentesCorretores = useSelector(state => state.corretor);
  const filtroSelecionado = useSelector(state => state.produtividade.filtroSelecionado);

  const formInitialValues = useMemo(() => ({
    ...initialValues,
    data_venda_inicio: (filtroSelecionado && filtroSelecionado.data_inicial) || '',
    data_venda_fim: (filtroSelecionado && filtroSelecionado.data_final) || '',
    codigo_empreendimento: (filtroSelecionado && filtroSelecionado.codigo_empreendimento) || '',
  }), [filtroSelecionado]);
  const totalPages = Math.ceil(vendas.length / recordsPerPage);

  const toggleModalDetalhes = () => {
    setAbrirModalDetalhes(!abrirModalDetalhes);
  };

  const selecionarLinha = linha => {
    setLinhaSelecionada(linha);
  };

  const optionsEmpreendimentos = [
    { value: '', label: 'Todos' },
    ...empreendimentos.sort.map(item => ({
      value: item.codigo,
      label: item.codigo + ' - ' + item.descricao,
    })),
  ];

  const optionsGerentes = [
    ...gerentesCorretores.listaGerentes.map(ger => ({
      value: ger.codigo,
      label: ger.nome_imobiliaria,
    })),
  ];

  const optionsCorretores = [
    ...gerentesCorretores.listaCorretores.map(cor => ({
      value: cor.codigo,
      label: cor.codigo + ' - ' + cor.nome,
    })),
  ];

  const toggleMaisFiltros = () => {
    setMaisFiltros(prev => !prev);
  };

  const getStatusOrder = (comissaoCancelada, comissaoBaixada) => {
    if (comissaoCancelada) return 3;
    if (comissaoBaixada) return 1;
    return 2;
  };

  const sortedVendas = useMemo(() => {
    if (sortConfig.key) {
      const sorted = [...vendas].sort((a, b) => {
        let aValue, bValue;

        if (sortConfig.key === 'status_comissao') {
          aValue = getStatusOrder(a.comissao_cancelada, a.comissao_baixada);
          bValue = getStatusOrder(b.comissao_cancelada, b.comissao_baixada);
        } else {
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
      return sorted;
    }
    return vendas;
  }, [vendas, sortConfig]);

  const currentRecords = useMemo(() => {
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    return sortedVendas.slice(indexOfFirstRecord, indexOfLastRecord);
  }, [sortedVendas, currentPage, recordsPerPage]);

  const buscarVendas = async filtroDTO => {
    try {
      setLoading(true);
      const response = await api.buscarRelatorioProdutividadeComissoes(
        filtroDTO
      );
      if (response) {
        setVendas(response.vendas);
        setTotais(response.totalizadores);
      }
    } catch (error) {
      toast.error(error.message || 'Erro ao buscar vendas');
    } finally {
      setLoading(false);
      setCurrentPage(1);
    }
  };

  const handleSubmit = values => {
    buscarVendas(values);
  };

  const handleSort = column => {
    let direction = 'asc';
    if (sortConfig.key === column && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: column, direction });
  };

  const handlePageChange = pageNumber => {
    setCurrentPage(pageNumber);
  };

  const exportarExcel = async () => {
    try {
      setExportandoExcel(true);
      await salvarEmExcel(vendas, '/exportador/excel/produtividade/comissoes');
      toast.success('Relatório exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar relatório');
    } finally {
      setExportandoExcel(false);
    }
  };

  const renderPagination = () => {
    const items = [];
    const maxVisiblePages = 5;
    const startPage = Math.max(
      1,
      currentPage - Math.floor(maxVisiblePages / 2)
    );
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }
    }

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  const buildValue = (options, value) => {
    const option = options.find(opt => opt.value === value);
    return option || options[0];
  };

  return (
    <div className="CardVendasDetalhadas">
      <Formik enableReinitialize initialValues={formInitialValues} onSubmit={handleSubmit}>
        {({
          handleSubmit,
          values,
          handleChange,
          setFieldValue,
          resetForm,
          errors,
          touched,
        }) => (
          <Col>
            <Card>
              <Card.Body>
                <form onSubmit={handleSubmit}>
                  <Row className="align-items-end">
                    <Col xs="6" sm="6" md="3" lg="2">
                      <DateMask
                        initialValue={values.data_venda_inicio}
                        allowEmpty
                        onValidate={value =>
                          setFieldValue('data_venda_inicio', value)
                        }
                      >
                        {({ dateError, dateValue, setDate }) => (
                          <>
                            <Form.Label>Data Venda Início</Form.Label>
                            <Form.Group controlId="filtro.data_venda_inicio">
                              <MaskedInput
                                name="data_venda_inicio"
                                type="tel"
                                guide={false}
                                className={`form-control ${((errors.data_venda_inicio &&
                                  touched.data_venda_inicio) ||
                                  dateError) &&
                                  'is-invalid'}`}
                                placeholder="Ex.: 01/01/2019"
                                mask={constants.MASK_DATE}
                                value={dateValue}
                                onChange={e => setDate(e.target.value)}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.data_venda_inicio || dateError}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </>
                        )}
                      </DateMask>
                    </Col>

                    <Col xs="6" sm="6" md="3" lg="2">
                      <DateMask
                        initialValue={values.data_venda_fim}
                        allowEmpty
                        onValidate={value =>
                          setFieldValue('data_venda_fim', value)
                        }
                      >
                        {({ dateError, dateValue, setDate }) => (
                          <>
                            <Form.Label>Data Venda Fim</Form.Label>
                            <Form.Group controlId="filtro.data_venda_fim">
                              <MaskedInput
                                name="data_venda_fim"
                                type="tel"
                                guide={false}
                                className={`form-control ${((errors.data_venda_fim &&
                                  touched.data_venda_fim) ||
                                  dateError) &&
                                  'is-invalid'}`}
                                placeholder="Ex.: 01/01/2019"
                                mask={constants.MASK_DATE}
                                value={dateValue}
                                onChange={e => setDate(e.target.value)}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.data_venda_fim || dateError}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </>
                        )}
                      </DateMask>
                    </Col>

                    <Col xs="12" sm="6" md="6" lg="4">
                      <Form.Group controlId="filtro.codigo_empreendimento">
                        <Form.Label>Empreendimento</Form.Label>
                        <Selector
                          isClearable
                          isLoading={optionsEmpreendimentos.length <= 1}
                          isDisabled={optionsEmpreendimentos.length <= 1}
                          styles={customStyles}
                          components={{ DropdownIndicator }}
                          noOptionsMessage={() =>
                            'Nenhum empreendimento encontrado'
                          }
                          placeholder="Selecione..."
                          options={optionsEmpreendimentos || []}
                          onChange={option => {
                            setFieldValue(
                              'codigo_empreendimento',
                              option ? option.value : ''
                            );
                          }}
                          value={
                            optionsEmpreendimentos.find(
                              opt => opt.value == values.codigo_empreendimento
                            ) || optionsEmpreendimentos[0]
                          }
                        />
                      </Form.Group>
                    </Col>

                    <Col xs="6" sm="6" md="6" lg="2">
                      <Form.Group controlId="filtro.status_comissao">
                        <Form.Label>Status comissão</Form.Label>
                        <Selector
                          styles={customStyles}
                          components={{ DropdownIndicator }}
                          placeholder="Selecione..."
                          options={[
                            { value: '', label: 'Todos' },
                            { value: '0', label: 'Recebida' },
                            { value: '1', label: 'A Receber' },
                            { value: '2', label: 'Cancelada' },
                          ]}
                          onChange={({ value }) =>
                            setFieldValue('status_comissao', value)
                          }
                          value={buildValue(
                            [
                              { value: '', label: 'Todos' },
                              { value: '0', label: 'Recebida' },
                              { value: '1', label: 'A Receber' },
                              { value: '2', label: 'Cancelada' },
                            ],
                            values.status_comissao
                          )}
                        />
                      </Form.Group>
                    </Col>

                    <Col xs="6" sm="6" md="6" lg="2">
                      <Form.Group controlId="filtro.refinanciado">
                        <Form.Label>Refinanciado</Form.Label>
                        <Selector
                          styles={customStyles}
                          components={{ DropdownIndicator }}
                          placeholder="Selecione..."
                          options={[
                            { value: '', label: 'Todos' },
                            { value: '1', label: 'Sim' },
                            { value: '0', label: 'Não' },
                          ]}
                          onChange={({ value }) =>
                            setFieldValue('refinanciado', value)
                          }
                          value={buildValue(
                            [
                              { value: '', label: 'Todos' },
                              { value: '1', label: 'Sim' },
                              { value: '0', label: 'Não' },
                            ],
                            values.refinanciado
                          )}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Collapse in={maisFiltros}>
                    <div>
                      <Row className="align-items-end mt-3">
                        <Col xs="12" sm="6" md="4" lg="4">
                          <Form.Group controlId="filtro.codigo_imobiliaria">
                            <Form.Label>Imobiliária</Form.Label>
                            <Selector
                              isClearable
                              isLoading={optionsGerentes.length === 0}
                              isDisabled={
                                optionsGerentes.optionsEmpreendimentos === 0 ||
                                AuthStorage.isCorretor() ||
                                AuthStorage.isGerente()
                              }
                              styles={customStyles}
                              components={{ DropdownIndicator }}
                              noOptionsMessage={() =>
                                'Nenhuma imobiliária encontrada'
                              }
                              placeholder="Pesquisar imobiliárias..."
                              options={optionsGerentes || []}
                              onChange={option => {
                                setFieldValue(
                                  'codigo_imobiliaria',
                                  option ? option.value : ''
                                );
                              }}
                              value={
                                optionsGerentes.find(
                                  opt => opt.value == values.codigo_imobiliaria
                                ) || null
                              }
                            />
                          </Form.Group>
                        </Col>

                        <Col xs="12" sm="6" md="4" lg="4">
                          <Form.Group controlId="filtro.codigo_corretor">
                            <Form.Label>Corretor</Form.Label>
                            <Selector
                              isClearable
                              isLoading={optionsCorretores.length === 0}
                              isDisabled={
                                optionsCorretores.optionsEmpreendimentos ===
                                  0 || AuthStorage.isCorretor()
                              }
                              styles={customStyles}
                              components={{ DropdownIndicator }}
                              noOptionsMessage={() =>
                                'Nenhum corretor encontrado'
                              }
                              placeholder="Pesquisar corretores..."
                              options={optionsCorretores || []}
                              onChange={option => {
                                setFieldValue(
                                  'codigo_corretor',
                                  option ? option.value : ''
                                );
                              }}
                              value={
                                optionsCorretores.find(
                                  opt => opt.value == values.codigo_corretor
                                ) || null
                              }
                            />
                          </Form.Group>
                        </Col>

                        <Col xs="12" sm="6" md="4" lg="2">
                          <DateMask
                            initialValue={values.data_proximo_pagamento_inicio}
                            allowEmpty
                            onValidate={value =>
                              setFieldValue(
                                'data_proximo_pagamento_inicio',
                                value
                              )
                            }
                          >
                            {({ dateError, dateValue, setDate }) => (
                              <>
                                <Form.Label>Próx. Pag. Início</Form.Label>
                                <Form.Group controlId="filtro.data_proximo_pagamento_inicio">
                                  <MaskedInput
                                    name="data_proximo_pagamento_inicio"
                                    type="tel"
                                    guide={false}
                                    className={`form-control ${((errors.data_proximo_pagamento_inicio &&
                                      touched.data_proximo_pagamento_inicio) ||
                                      dateError) &&
                                      'is-invalid'}`}
                                    placeholder="Ex.: 01/01/2019"
                                    mask={constants.MASK_DATE}
                                    value={dateValue}
                                    onChange={e => setDate(e.target.value)}
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {errors.data_proximo_pagamento_inicio ||
                                      dateError}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </>
                            )}
                          </DateMask>
                        </Col>

                        <Col xs="12" sm="6" md="4" lg="2">
                          <DateMask
                            initialValue={values.data_proximo_pagamento_fim}
                            allowEmpty
                            onValidate={value =>
                              setFieldValue('data_proximo_pagamento_fim', value)
                            }
                          >
                            {({ dateError, dateValue, setDate }) => (
                              <>
                                <Form.Label>Próx. Pag. Fim</Form.Label>
                                <Form.Group controlId="filtro.data_proximo_pagamento_fim">
                                  <MaskedInput
                                    name="data_proximo_pagamento_fim"
                                    type="tel"
                                    guide={false}
                                    className={`form-control ${((errors.data_proximo_pagamento_fim &&
                                      touched.data_proximo_pagamento_fim) ||
                                      dateError) &&
                                      'is-invalid'}`}
                                    placeholder="Ex.: 01/01/2019"
                                    mask={constants.MASK_DATE}
                                    value={dateValue}
                                    onChange={e => setDate(e.target.value)}
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {errors.data_proximo_pagamento_fim ||
                                      dateError}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </>
                            )}
                          </DateMask>
                        </Col>
                      </Row>

                      <Row className="align-items-end mt-3">
                        <Col xs="6" sm="6" md="3" lg="2">
                          <Form.Group controlId="filtro.codigo_prospecto">
                            <Form.Label>Cód. Prospecto</Form.Label>
                            <Form.Control
                              type="text"
                              name="codigo_prospecto"
                              placeholder="Código"
                              onChange={handleChange}
                              value={values.codigo_prospecto}
                            />
                          </Form.Group>
                        </Col>

                        <Col xs="6" sm="6" md="3" lg="2">
                          <Form.Group controlId="filtro.codigo_prevenda">
                            <Form.Label>Cód. Pré-venda</Form.Label>
                            <Form.Control
                              type="text"
                              name="codigo_prevenda"
                              placeholder="Código"
                              onChange={handleChange}
                              value={values.codigo_prevenda}
                            />
                          </Form.Group>
                        </Col>

                        <Col xs="6" sm="6" md="3" lg="2">
                          <Form.Group controlId="filtro.quadra">
                            <Form.Label>Quadra</Form.Label>
                            <Form.Control
                              type="text"
                              name="quadra"
                              placeholder="Quadra"
                              onChange={handleChange}
                              value={values.quadra}
                            />
                          </Form.Group>
                        </Col>

                        <Col xs="6" sm="6" md="3" lg="2">
                          <Form.Group controlId="filtro.lote">
                            <Form.Label>Lote</Form.Label>
                            <Form.Control
                              type="text"
                              name="lote"
                              placeholder="Lote"
                              onChange={handleChange}
                              value={values.lote}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  </Collapse>

                  <Row className="mt-3">
                    <Col xs="12" className="d-flex justify-content-end">
                      <Button
                        variant="outline-secondary"
                        onClick={toggleMaisFiltros}
                        className="mr-2"
                      >
                        {maisFiltros ? <FaMinus /> : <FaPlus />}
                        <span>&nbsp;Filtros</span>
                      </Button>
                      <Button
                        variant="outline-danger"
                        className="mr-2"
                        onClick={() => {
                          resetForm();
                          setVendas([]);
                          setTotais({
                            quantidade_vendas: 0,
                            valor_total_vendas: 0,
                            total_comissao_gerada: 0,
                            total_comissao_baixada: 0,
                            total_comissao_em_aberto: 0,
                            total_comissao_cancelada: 0,
                          });
                        }}
                      >
                        <FaEraser />
                        <span>&nbsp;Limpar</span>
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="mr-2"
                            />
                            Buscando...
                          </>
                        ) : (
                          <>
                            <FaSearch />
                            <span>&nbsp;Buscar</span>
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>
                </form>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Formik>

      {vendas.length > 0 ? (
        <>
          {totais && (
            <div className="container-fluid px-3 px-md-4 mt-4">
              <Card className="totalizadores-card">
                <Card.Body className="py-3">
                  <Row className="totalizadores-row">
                    <Col xs="6" sm="4" lg="2" className="totalizador-item">
                      <small className="totalizador-label">Qtd. Vendas</small>
                      <strong className="totalizador-valor">
                        {totais.quantidade_vendas.toLocaleString('pt-BR') || 0}
                      </strong>
                    </Col>

                    <Col xs="6" sm="4" lg="2" className="totalizador-item">
                      <small className="totalizador-label">
                        Valor Total Vendas
                      </small>
                      <strong className="totalizador-valor">
                        {formatarMonetario(totais.valor_total_vendas)}
                      </strong>
                    </Col>

                    <Col xs="6" sm="4" lg="2" className="totalizador-item">
                      <small className="totalizador-label">
                        Comissão Gerada
                      </small>
                      <strong className="totalizador-valor">
                        {formatarMonetario(totais.total_comissao_gerada) ||
                          formatarMonetario(0)}
                      </strong>
                    </Col>

                    <Col xs="6" sm="4" lg="2" className="totalizador-item">
                      <small className="totalizador-label">
                        Comissão Baixada
                      </small>
                      <strong className="totalizador-valor">
                        {formatarMonetario(totais.total_comissao_baixada) ||
                          formatarMonetario(0)}
                      </strong>
                    </Col>

                    <Col xs="6" sm="4" lg="2" className="totalizador-item">
                      <small className="totalizador-label">
                        Comissão em Aberto
                      </small>
                      <strong className="totalizador-valor">
                        {formatarMonetario(totais.total_comissao_em_aberto) ||
                          formatarMonetario(0)}
                      </strong>
                    </Col>

                    <Col xs="6" sm="4" lg="2" className="totalizador-item">
                      <small className="totalizador-label">
                        Comissão Cancelada
                      </small>
                      <strong className="totalizador-valor">
                        {formatarMonetario(totais.total_comissao_cancelada) ||
                          formatarMonetario(0)}
                      </strong>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </div>
          )}

          <div className="container-fluid px-3 px-md-4 mt-4">
            <div className="bg-white p-3 rounded">
              <Row className="d-flex justify-content-between align-items-center">
                <Col>
                  <span>
                    Total de {vendas.length} registro(s) encontrado(s).
                  </span>
                </Col>
                <Col xs="auto">
                  <Button
                    variant="success"
                    onClick={exportarExcel}
                    disabled={exportandoExcel || loading}
                  >
                    {exportandoExcel ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="mr-2"
                        />
                        Exportando...
                      </>
                    ) : (
                      <>
                        <FaFileExcel className="mr-2" />
                        Exportar Excel
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
            </div>
          </div>

          <div className="container-fluid px-3 px-md-4 mt-3">
            <div className="bg-white table-responsive rounded">
              <Table className="table table-sm table-striped table-bordered table-hover mb-0">
                <Thead>
                  <Tr className="text-center" style={{ fontSize: '14px' }}>
                    <Th
                      onClick={() => handleSort('data_venda')}
                      style={{ cursor: 'pointer' }}
                    >
                      Data Venda
                    </Th>
                    <Th
                      onClick={() => handleSort('codigo_titulo')}
                      style={{ cursor: 'pointer' }}
                    >
                      Título Comissão
                    </Th>
                    <Th
                      onClick={() => handleSort('pre_venda')}
                      style={{ cursor: 'pointer' }}
                    >
                      Pré-venda
                    </Th>
                    <Th
                      onClick={() => handleSort('imobiliaria')}
                      style={{ cursor: 'pointer' }}
                    >
                      Imobiliária
                    </Th>
                    <Th
                      onClick={() => handleSort('corretor')}
                      style={{ cursor: 'pointer' }}
                    >
                      Corretor
                    </Th>
                    <Th
                      onClick={() => handleSort('empreendimento')}
                      style={{ cursor: 'pointer' }}
                    >
                      Empreendimento - QD / LT
                    </Th>
                    <Th
                      onClick={() => handleSort('nome_prospecto')}
                      style={{ cursor: 'pointer' }}
                    >
                      Cliente - Cód. Prospecto / GE
                    </Th>
                    <Th
                      onClick={() => handleSort('status_comissao')}
                      style={{ cursor: 'pointer' }}
                    >
                      Status Comissão
                    </Th>
                    <Th
                      onClick={() => handleSort('data_proximo_pagamento')}
                      style={{ cursor: 'pointer' }}
                    >
                      Próx. Pagamento (comissão)
                    </Th>
                    <Th>Refinanciado</Th>
                    <Th>Detalhes</Th>
                  </Tr>
                </Thead>
                <Tbody className="text-center" style={{ fontSize: '12px' }}>
                  {currentRecords.map((venda, index) => (
                    <Tr
                      key={index}
                      style={{
                        fontWeight:
                          linhaSelecionada === venda ? 'bold' : 'normal',
                        cursor: 'pointer',
                        transition: 'background-color 0.1s ease',
                      }}
                      onClick={() => selecionarLinha(venda)}
                    >
                      <Td>{format.date(venda.data_venda)}</Td>
                      <Td>{venda.codigo_titulo}</Td>
                      <Td>{venda.pre_venda}</Td>
                      <Td className="text-left">
                        {venda.imobiliaria || 'Não informado'}
                      </Td>
                      <Td className="text-left">
                        {venda.corretor || 'Não informado'}
                      </Td>
                      <Td className="text-left">
                        {venda.empreendimento} - {venda.quadra} / {venda.lote}
                      </Td>
                      <Td className="text-left">
                        {venda.nome_prospecto} - {venda.codigo_prospecto} /{' '}
                        {venda.ge}
                      </Td>
                      <Td>
                        {renderBadgeStatusComissao(
                          venda.comissao_cancelada,
                          venda.comissao_baixada
                        )}
                      </Td>
                      <Td>{format.date(venda.data_proximo_pagamento)}</Td>
                      <Td>{venda.tem_refinanciamento === 1 ? 'Sim' : 'Não'}</Td>
                      <Td className="text-center">
                        <IconButton
                          onClick={() => {
                            toggleModalDetalhes();
                          }}
                          title="Ver detalhes"
                        >
                          <FaSearchDollar size={16} />
                        </IconButton>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </div>
          </div>

          <div className="container-fluid px-3 px-md-4 mt-3 mb-4">
            <div className="bg-white p-3 rounded">
              <Row className="d-flex justify-content-between align-items-center">
                <Col xs="auto">
                  {totalPages > 1 && (
                    <Pagination className="mb-0">
                      <Pagination.First
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                      />
                      <Pagination.Prev
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      />
                      {renderPagination()}
                      <Pagination.Next
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      />
                      <Pagination.Last
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                      />
                    </Pagination>
                  )}
                </Col>
              </Row>
            </div>
          </div>
          {abrirModalDetalhes && (
            <ModalDetalhesComissao
              mostrarModal={abrirModalDetalhes}
              setMostrarModal={setAbrirModalDetalhes}
              linhaSelecionada={linhaSelecionada}
            />
          )}
        </>
      ) : (
        !loading && (
          <div className="container-fluid px-3 px-md-4 mt-3">
            <div className="bg-white p-5 text-center rounded">
              <Row>
                <Col>
                  <FaExclamationCircle size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">Nenhum registro encontrado.</h5>
                  <p className="text-muted">
                    Utilize os filtros acima para buscar dados.
                  </p>
                </Col>
              </Row>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default CardVendasDetalhadas;
